import { useEffect, useState } from 'react';
import { X, AlertTriangle, AlertCircle, CheckCircle, MinusCircle, Loader2 } from 'lucide-react';
import irrigationNetworkService, {
  type ProgramAnalysis,
  type ProgramAnalysisStep,
} from '../../../services/irrigationNetwork';
import { errorMessage } from '../../../utils/apiResponse';

interface Props {
  programId: string;
  onClose: () => void;
}

export default function ScheduleAnalysisModal({ programId, onClose }: Props) {
  const [data, setData] = useState<ProgramAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await irrigationNetworkService.analyseProgram(Number(programId));
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Analysis failed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [programId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white">
          <h2 className="font-semibold">Hydraulic Check — {data?.program.name ?? '…'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Solving each step…
            </div>
          )}
          {error && (
            <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-3 rounded">{error}</div>
          )}
          {data && data.meta.skipped && (
            <div className="text-amber-700 text-sm bg-amber-50 border border-amber-200 p-3 rounded">
              {data.meta.skip_reason}
            </div>
          )}
          {data && !data.meta.skipped && (
            <>
              <div className="mb-4 flex flex-wrap gap-3 text-xs">
                <Stat label="Steps" value={data.meta.total_steps ?? 0} />
                <Stat label="OK"     value={data.meta.step_status_counts?.ok ?? 0}    color="green" />
                <Stat label="Warn"   value={data.meta.step_status_counts?.warn ?? 0}  color="amber" />
                <Stat label="Error"  value={data.meta.step_status_counts?.error ?? 0} color="red"   />
                <Stat label="Empty"  value={data.meta.step_status_counts?.empty ?? 0} color="gray"  />
              </div>

              <div className="space-y-2">
                {data.steps.length === 0 && (
                  <div className="text-sm text-gray-500">Program has no steps.</div>
                )}
                {data.steps.map((s) => (
                  <StepRow key={s.step_id} step={s}
                    open={openStep === s.step_id}
                    onToggle={() => setOpenStep(openStep === s.step_id ? null : s.step_id)} />
                ))}
              </div>

              <div className="mt-4 text-xs text-gray-500 border-t pt-3">
                Each step is solved in isolation against the farm's irrigation network. Manual valves
                are not included — to add them, link the schedule's relays to valve nodes on the network map.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color?: 'green' | 'amber' | 'red' | 'gray' }) {
  const cls = color === 'green' ? 'bg-green-100 text-green-700' :
              color === 'amber' ? 'bg-amber-100 text-amber-700' :
              color === 'red'   ? 'bg-red-100 text-red-700' :
              color === 'gray'  ? 'bg-gray-100 text-gray-700' :
                                  'bg-blue-50 text-blue-700';
  return (
    <div className={`px-2 py-1 rounded ${cls}`}>
      <span className="font-semibold">{value}</span> {label}
    </div>
  );
}

function StepRow({ step, open, onToggle }: { step: ProgramAnalysisStep; open: boolean; onToggle: () => void }) {
  const icon =
    step.summary.status === 'error' ? <AlertCircle className="w-4 h-4 text-red-600" /> :
    step.summary.status === 'warn'  ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
    step.summary.status === 'empty' ? <MinusCircle className="w-4 h-4 text-gray-400" /> :
                                      <CheckCircle className="w-4 h-4 text-green-600" />;

  return (
    <div className="border rounded">
      <button className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50" onClick={onToggle}>
        {icon}
        <div className="flex-1 text-left">
          <span className="font-medium">Step {step.step_order + 1}</span>
          <span className="text-gray-500 ml-2">
            {step.summary.valve_count} valve{step.summary.valve_count !== 1 ? 's' : ''}
            {step.summary.unmapped_count > 0 && ` · ${step.summary.unmapped_count} unmapped`}
            {step.summary.max_velocity_ms > 0 && ` · max ${step.summary.max_velocity_ms} m/s`}
            {step.summary.min_pressure_kpa != null && ` · min ${step.summary.min_pressure_kpa} kPa`}
          </span>
        </div>
        <span className="text-xs text-gray-400">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 text-xs space-y-2 border-t">
          {step.mapped.length > 0 && (
            <div>
              <div className="font-semibold text-gray-700">Open valves</div>
              <ul className="list-disc list-inside text-gray-600">
                {step.mapped.map((m) => (
                  <li key={`${m.router_mac}:${m.relay_index}`}>
                    {m.node_name} <span className="text-gray-400">({m.router_mac}:{m.relay_index})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step.pumps && step.pumps.length > 0 && (
            <div>
              <div className="font-semibold text-gray-700">Pumps started</div>
              <ul className="list-disc list-inside text-gray-600">
                {step.pumps.map((m) => (
                  <li key={`${m.router_mac}:${m.relay_index}`}>
                    {m.node_name} <span className="text-gray-400">({m.router_mac}:{m.relay_index})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {step.unmapped.length > 0 && (
            <div>
              <div className="font-semibold text-amber-700">Unmapped relays (no valve or pump node linked)</div>
              <ul className="list-disc list-inside text-amber-600">
                {step.unmapped.map((u) => (
                  <li key={`${u.router_mac}:${u.relay_index}`}>{u.router_mac}:{u.relay_index}</li>
                ))}
              </ul>
            </div>
          )}

          {step.result?.warnings && step.result.warnings.length > 0 && (
            <div>
              <div className="font-semibold text-gray-700">Warnings</div>
              <ul className="space-y-0.5">
                {step.result.warnings.map((w, i) => (
                  <li key={i} className={
                    w.level === 'error' ? 'text-red-700' :
                    w.level === 'warn'  ? 'text-amber-700' : 'text-blue-700'
                  }>• {w.message}</li>
                ))}
              </ul>
            </div>
          )}

          {step.result && step.result.warnings.length === 0 && step.summary.status !== 'empty' && (
            <div className="text-green-700">All checks pass.</div>
          )}

          {step.summary.status === 'empty' && (
            <div className="text-gray-500">No valves linked — nothing to solve.</div>
          )}
        </div>
      )}
    </div>
  );
}
